import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Bot, Loader2, Send, Sparkles, Trash2 } from "lucide-react";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { api, streamAssistant } from "@/lib/api";

const QUICK = ["Résume l'état de l'atelier aujourd'hui", "Quels dossiers risquent de dépasser leur date de restitution ?", "Y a-t-il des pièces consommées absentes d'un devis ?", "Quelles interventions réalisées ne sont pas facturées ?"];
const SOURCE_PATH = { repair_order: (id) => `/atelier/${id}`, vehicle: (id) => `/vehicules/${id}`, client: (id) => `/clients/${id}`, quote: (id) => `/devis/detail/${id}`, invoice: (id) => `/factures?open=${id}`, dashboard: () => "/tableau-de-bord" };

export function AiAssistant({ open, onOpenChange }) {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const endRef = useRef(null);

  useEffect(() => {
    if (open) api.get("/ai/assistant/history").then((r) => setMessages(r.data.map((m) => ({ role: m.role, content: m.content, sources: m.sources || [] })))).catch(() => {});
  }, [open]);

  useEffect(() => endRef.current?.scrollIntoView({ behavior: "smooth" }), [messages, busy]);

  const contextFromPath = () => {
    const ro = location.pathname.match(/^\/atelier\/([a-f0-9]{24})/);
    return ro ? { context_type: "repair_order", context_id: ro[1] } : {};
  };

  const send = async (text) => {
    const message = (text ?? input).trim();
    if (!message || busy) return;
    setInput("");
    setBusy(true);
    setMessages((m) => [...m, { role: "user", content: message }, { role: "assistant", content: "", sources: [] }]);
    const update = (fn) => setMessages((m) => { const copy = [...m]; copy[copy.length - 1] = fn(copy[copy.length - 1]); return copy; });
    await streamAssistant({ message, ...contextFromPath() }, {
      onSources: (s) => update((a) => ({ ...a, sources: s })),
      onDelta: (d) => update((a) => ({ ...a, content: a.content + d })),
      onError: (e) => update((a) => ({ ...a, content: a.content + `\n\n⚠ ${e}` })),
      onDone: () => setBusy(false),
    });
    setBusy(false);
  };

  const clear = async () => {
    await api.delete("/ai/assistant/history");
    setMessages([]);
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="flex w-full flex-col gap-0 p-0 sm:max-w-lg" data-testid="ai-assistant-drawer">
        <SheetHeader className="border-b border-border px-5 py-4">
          <SheetTitle className="flex items-center gap-2 font-heading">
            <span className="grid h-8 w-8 place-items-center rounded-lg bg-primary/15 text-primary"><Sparkles className="h-4 w-4" /></span>
            Assistant atelier
            <span className="ml-auto text-xs font-normal text-muted-foreground">GPT-5.4 · données réelles uniquement</span>
          </SheetTitle>
        </SheetHeader>
        <div className="flex-1 space-y-4 overflow-y-auto px-5 py-4">
          {messages.length === 0 && (
            <div className="space-y-3">
              <p className="text-sm text-muted-foreground">Je réponds uniquement à partir des dossiers du CRM et je distingue faits, suggestions et informations manquantes. Aucune action n'est déclenchée sans validation.</p>
              <div className="grid gap-2">
                {QUICK.map((q) => (
                  <button key={q} onClick={() => send(q)} data-testid="ai-quick-prompt" className="rounded-xl border border-border bg-secondary/40 px-3 py-2 text-left text-sm transition-colors hover:border-primary/50 hover:bg-primary/5">{q}</button>
                ))}
              </div>
            </div>
          )}
          {messages.map((m, i) => (
            <div key={i} className={m.role === "user" ? "flex justify-end" : "flex justify-start"}>
              <div className={`max-w-[92%] rounded-2xl px-4 py-3 text-sm leading-relaxed ${m.role === "user" ? "bg-primary text-primary-foreground" : "bg-secondary/70"}`} data-testid={`ai-message-${m.role}`}>
                {m.role === "assistant" && <Bot className="mb-1 h-4 w-4 text-primary" />}
                <div className="whitespace-pre-wrap">{m.content || (busy && i === messages.length - 1 ? <span className="inline-flex items-center gap-2 text-muted-foreground"><Loader2 className="h-3.5 w-3.5 animate-spin" /> Analyse des dossiers…</span> : "")}</div>
                {m.sources?.length > 0 && (
                  <div className="mt-3 flex flex-wrap gap-1.5 border-t border-border/50 pt-2">
                    {m.sources.map((s, j) => (
                      <button key={j} onClick={() => { onOpenChange(false); navigate(SOURCE_PATH[s.type]?.(s.id) || "/"); }} data-testid="ai-source-link" className="rounded-full border border-primary/40 bg-primary/10 px-2 py-0.5 text-[11px] font-semibold text-primary transition-colors hover:bg-primary/20">{s.label}</button>
                    ))}
                  </div>
                )}
              </div>
            </div>
          ))}
          <div ref={endRef} />
        </div>
        <form onSubmit={(e) => { e.preventDefault(); send(); }} className="flex items-center gap-2 border-t border-border p-3">
          <Button type="button" variant="ghost" size="icon" onClick={clear} title="Effacer l'historique" data-testid="ai-clear-button"><Trash2 className="h-4 w-4" /></Button>
          <input value={input} onChange={(e) => setInput(e.target.value)} placeholder="Posez une question sur un dossier, une plaque, un devis…" data-testid="ai-assistant-input" className="h-11 flex-1 rounded-full border border-border bg-secondary/50 px-4 text-sm outline-none focus:border-primary/60" />
          <Button type="submit" disabled={busy || !input.trim()} className="h-11 rounded-full px-4" data-testid="ai-assistant-send-button"><Send className="h-4 w-4" /></Button>
        </form>
      </SheetContent>
    </Sheet>
  );
}
